import { TOffer } from "types";

/**
 * Berechnet die maximal nötige Anzahlung (bzw. Leasing-Sonderzahlung)
 * basierend auf dem Vertragstyp und einer Ziel-Wunschrate.
 * Ohne integrierte Daten-Bereinigung.
 */
export const calculateMaxDownPayment = (
  offer: TOffer,
  runtime: number,
  targetRate: number,
): number => {
  const {
    financeType,
    rate,
    downPayment,
    borrowingRate,
    netLoanAmount,
    closingRate: rawClosingRate,
  } = offer;
  // --- TRAKT 1: LEASING (Linearer Ansatz) ---
  if (financeType === "leasing") {
    if (targetRate >= rate) return 0;

    const monthlyDifference = rate - targetRate;
    const estimatedDownPayment = downPayment + monthlyDifference * runtime;

    // Einbeziehung des marktüblichen Zinsvorteils bei Leasing-Anzahlungen (ca. 3%)
    const leasingZinsVorteil = 0.97;
    return Math.round(estimatedDownPayment * leasingZinsVorteil * 100) / 100;
  }

  // --- TRAKT 2: FINANZIERUNG (Kredit & Ballon via Barwert) ---
  // Bei einer klassischen Raten-Finanzierung ('credit') gibt es keine Schlussrate
  const closingRate = financeType === "credit" ? 0 : rawClosingRate ?? 0;

  const annualRate = borrowingRate / 100;
  const monthlyRate = annualRate / 12;

  let presentValueRates = 0;
  let presentValueClosingRate = 0;

  if (monthlyRate === 0) {
    presentValueRates = targetRate * runtime;
    presentValueClosingRate = closingRate;
  } else {
    // Mathematische Barwert-Berechnung (Present Value)
    presentValueRates =
      targetRate * ((1 - (1 + monthlyRate) ** -runtime) / monthlyRate);
    presentValueClosingRate = closingRate / (1 + monthlyRate) ** runtime;
  }

  // Ermittlung der nötigen Anzahlungs-Differenz
  const coveredLoanAmount = presentValueRates + presentValueClosingRate;
  const totalVehicleCreditNeed = netLoanAmount + downPayment;
  const maxDownPayment = totalVehicleCreditNeed - coveredLoanAmount;

  // Reiner mathematischer Plausibilitäts-Check des Endergebnisses
  if (maxDownPayment < 0) {
    throw new Error(
      `❌ Wunschrate von ${targetRate} € ist mathematisch zu hoch für diesen Fahrzeugwert.`,
    );
  }
  return Math.round(maxDownPayment * 100) / 100;
};
